import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { SupportQr } from '@/components/organisms/self-checkin/payment/support-qr'
import { translateObject } from '@/libs/text'
import dayjs from 'dayjs'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { LoaderContextCreator } from '@/contexts/loader'
import { useGuestAppUrl } from '@/hooks/use-guest-app-url'
import { cashChangerStorageKey, terminalStorageKey } from '@/constants/self-checkin-device-storage'
import { CalcCashChangerModal } from '@/components/molecules/front-tablet/payment/calc-cash-changer-modal'
import { CASH, CASH_CHANGER } from '@/models/payment-method'
import { PaymentResultByCash } from '@/models/self-checkin'
import { selfCheckinHandlePaymentByCash } from '@/apis/aipass'
import { reportError } from '@/components/apps/error-boundary'
import { useReceiptFormModal } from '@/components/molecules/front-tablet/payment/use-receipt-form-modal'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'

type SalesType = {
  id: string
  salesDate: string
  salesSubjectName: string
  salesSubjectNameEn: string
  salesSubjectNameKo: string
  salesSubjectNameZh: string
  salesSubjectNameZhTw: string
  salesPrice: number
}
type PriceSummaryType = {
  totalPrice: number
  totalPayment: number
  totalTaxPrice: number
  totalDiscount: number
  totalAmount: number
}

type PaymentMethodButtonProps = {
  onClick: () => void
  children: React.ReactNode
  isLongText?: boolean
}

const paymentMethodButtonStyle = css(
  {
    fontFamily: 'Noto Sans JP, sans-serif',
    fontWeight: 'bold',
    lineHeight: 'normal',
    letterSpacing: '1px',
    textAlign: 'center',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { height: 51 },
  }),
)

const PaymentMethodLongTextStyle = css(
  paymentMethodButtonStyle,
  { fontSize: 17, lineHeight: 1.2 },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 21 },
  }),
)

const PaymentMethodButton: React.FC<PaymentMethodButtonProps> = ({ onClick, children, isLongText }) => (
  <Button
    buttonType={ButtonType.Primary}
    width="100%"
    height={53}
    fontSize={20}
    borderRadius={10}
    onClick={onClick}
    customCss={isLongText ? PaymentMethodLongTextStyle : paymentMethodButtonStyle}
  >
    {children}
  </Button>
)

export const TerminalPaymentScreen: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, saveState, doCheckIn } = useSelfCheckInState()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { mypageUrl, homePageUrl } = useGuestAppUrl({
    hotelId: state.hotelId,
    plugin: state.setting.plugin,
  })
  const { openReceiptFormModal, ReceiptFormModal } = useReceiptFormModal()

  const [isProcessingCashChanger, setIsProcessingCashChanger] = useState(false)
  const [reservationWithSales, setReservationWithSales] = useState<Array<{ reservationId: string; sales: Array<SalesType> }>>()
  const [priceSummary, setPriceSummary] = useState<PriceSummaryType>()

  const hasCashChanger = (localStorage.getItem(cashChangerStorageKey)?.length || 0) > 0
  const hasCreditTerminal = (localStorage.getItem(terminalStorageKey)?.length || 0) > 0
  const showCashButton = hasCashChanger || state.setting.paymentTerminal.isEnabledCash

  const initializeState = async () => {
    setReservationWithSales(state.confirm?.payment?.usage.reservations.filter(r => r.sales.length || r.salesDiscounts.length))
    setPriceSummary(state.confirm?.payment?.usage)
  }

  const doCheckinWithoutPayment = async (paymentResult?: PaymentResultByCash, targetState = state) => {
    try {
      setIsLoading(true)
      await doCheckIn({
        state: targetState,
        language: i18n.language,
        guestAppUrl: { mypageUrl, homePageUrl },
        selectedPaymentMethod: paymentResult ? CASH_CHANGER : CASH,
        paymentResult,
      })
      history.replace({ pathname: '/self-checkin/issue-room-key' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    } finally {
      setIsLoading(false)
    }
  }

  const confirmReceiptName = async (onConfirmed: (nextState: typeof state) => void) => {
    const res = await openReceiptFormModal()
    if (!res.isConfirmed) {
      return
    }
    const nextState = {
      ...state,
      confirm: {
        ...state.confirm!,
        payment: {
          ...state.confirm!.payment!,
          receiptName: res.billingAddress,
        },
      },
    }
    saveState(nextState)
    onConfirmed(nextState)
  }

  useEffect(() => {
    initializeState()
  }, [])

  if (!state.confirm?.payment) {
    return <></>
  }

  return (
    <SelfCheckinLayout state={state} header={{ goToPreviousPage: history.goBack, title: t('Payment details') }}>
      <div css={paymentColumnsStyle}>
        <div css={salesSubjectBlock} className="sales-detail-column">
          <div className="block-title">{t('Usage details')}</div>
          <div className="reserve-and-sales-block">
            {reservationWithSales?.map((r, i) => (
              <div key={r.reservationId}>
                <div className="reservation-row">
                  <div>{`${t('Reserve')}${i + 1}`}</div>
                  <div>{`${t('Reservation ID')} : ${r.reservationId}`}</div>
                </div>
                <div>
                  {r.sales.map(s => (
                    <div key={s.id} className="sales-row">
                      <div>
                        <div>{dayjs(s.salesDate).format('MM/DD')}</div>
                        <div>{translateObject(s, 'salesSubjectName', i18n.language)}</div>
                      </div>
                      <div>¥{s.salesPrice.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="payment-column">
          <div css={amountBlock}>
            <div className="detail-block">
              <div className="price-row">
                <div>{t('Amount used')}</div>
                <div className="-price">¥{priceSummary?.totalPrice.toLocaleString()}</div>
              </div>
              <div className="price-row">
                <div>{t('Discount')}</div>
                <div className="-price">¥{priceSummary?.totalDiscount.toLocaleString()}</div>
              </div>
            </div>
            <div className="total-block">
              <div className="price-row">
                <div>{t('Amount billed')}</div>
                <div className="-price">¥{priceSummary?.totalAmount.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div css={paymentMethodBlock}>
            <div css={paymentButtonsStyle} className={hasCreditTerminal ? 'two-columns' : 'one-column'}>
              {hasCreditTerminal && (
                <>
                  <PaymentMethodButton
                    onClick={async () => {
                      await confirmReceiptName(() => history.replace({ pathname: '/self-checkin/payment/terminal/card' }))
                    }}
                  >
                    {t('Credit Card')}
                  </PaymentMethodButton>
                  <PaymentMethodButton
                    onClick={async () => {
                      await confirmReceiptName(() =>
                        history.replace({ pathname: '/self-checkin/payment/terminal/card', search: 'unionPay=1' }),
                      )
                    }}
                  >
                    {t('UnionPay')}
                  </PaymentMethodButton>
                  {state.setting.paymentTerminal.isEnabledQr && (
                    <PaymentMethodButton
                      onClick={async () => {
                        await confirmReceiptName(() => history.replace({ pathname: '/self-checkin/payment/terminal/qr' }))
                      }}
                    >
                      {t('QR Payment')}
                    </PaymentMethodButton>
                  )}
                </>
              )}
              {showCashButton &&
                (hasCashChanger ? (
                  <PaymentMethodButton
                    onClick={async () => {
                      await confirmReceiptName(() => setIsProcessingCashChanger(true))
                    }}
                  >
                    <Trans i18nKey="Cash" />
                  </PaymentMethodButton>
                ) : (
                  <PaymentMethodButton
                    onClick={async () => {
                      // フロント現金は対面精算のため、この場で宛名を聞いても領収書・メールに渡らず再確認の手間だけが増える
                      await doCheckinWithoutPayment()
                    }}
                    isLongText
                  >
                    <Trans i18nKey="Cash payment at front desk" />
                  </PaymentMethodButton>
                ))}
            </div>
            {hasCreditTerminal && (
              <>
                <div className="credit-block">
                  <div className="payment-title">{t('Supported Credits')}</div>
                  <img src={require('@/static/images/self-checkin-payment/terminal/logo_card01.png')} />
                </div>
                {state.setting.paymentTerminal.isEnabledQr && <SupportQr className="brand-image-block" />}
              </>
            )}
          </div>
        </div>
      </div>
      <CalcCashChangerModal
        isVisibility={isProcessingCashChanger}
        hotelId={state.hotelId}
        totalPrice={Number(state.confirm.payment.usage.totalAmount)}
        doPayment={() =>
          selfCheckinHandlePaymentByCash({
            hotelId: state.hotelId,
            reservationIdWithAssignIds: state.selectReservation!.selectedReservationAndRooms,
            paymentPrice: Number(state.confirm!.payment!.usage.totalAmount),
          })
        }
        onCancel={() => setIsProcessingCashChanger(false)}
        onComplete={paymentResult => {
          setIsProcessingCashChanger(false)
          doCheckinWithoutPayment(paymentResult)
        }}
      />
      <ReceiptFormModal billingAddress={state.confirm.payment.receiptName} />
    </SelfCheckinLayout>
  )
}

const paymentColumnsStyle = css(
  {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 624fr) minmax(0, 413fr)',
    gap: 24,
    height: '100%',
    '*': {
      fontFamily: 'Noto Sans JP',
      color: '#272727',
    },
    '.payment-column': { display: 'flex', flexDirection: 'column', gap: 16 },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      gridTemplateColumns: 'minmax(0, 482fr) minmax(0, 319fr)',
      gap: 19,
    },
    landscape13: {
      gridTemplateColumns: 'minmax(0, 723fr) minmax(0, 479fr)',
      gap: 28,
    },
  }),
)
const blockLayout = css(
  {
    backgroundColor: '#ffffff',
    borderRadius: 10,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { borderRadius: 0 },
  }),
)
const salesSubjectBlock = css(
  blockLayout,
  {
    height: 707,
    '.block-title': {
      padding: '16px 24px',
      fontWeight: 'bold',
      fontSize: 22,
      letterSpacing: 1.1,
      lineHeight: '30px',
    },
    '.reserve-and-sales-block': {
      overflow: 'auto',
      height: 'calc(100% - 62px)',
    },
    '.reservation-row': {
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: '#F5F5F5',
      padding: '6px 24px',
      fontWeight: 'bold',
      fontSize: 14,
      letterSpacing: 0.7,
      lineHeight: '19px',
    },
    '.sales-row': {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '17px 24px',
      fontSize: 16,
      letterSpacing: 0.8,
      lineHeight: '22px',
      borderBottom: '1px solid #F2F2F2',
      'div:first-of-type': { display: 'flex', gap: 24 },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      '.block-title': { paddingLeft: 19, paddingRight: 19 },
    },
    landscape13: {
      '.block-title': { padding: '16px 40px', fontSize: 26, lineHeight: '35px', letterSpacing: 1.3 },
      '.reserve-and-sales-block': { height: 'calc(100% - 67px)' },
      '.reservation-row': { padding: '6px 40px', fontSize: 18, letterSpacing: 0.9, lineHeight: '25px' },
      '.sales-row': { padding: '17px 40px', fontSize: 20, letterSpacing: 1, lineHeight: '27px' },
    },
  }),
)
const amountBlock = css(
  blockLayout,
  {
    '.price-row': {
      display: 'flex',
      justifyContent: 'space-between',
    },
    '.detail-block': {
      padding: '18px 24px',
      fontSize: 16,
      letterSpacing: 0.8,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      '.-price': { fontSize: 18, letterSpacing: 0.9 },
    },
    '.total-block': {
      borderTop: '1px solid #CCCCCC',
      padding: '18px 24px',
      fontWeight: 'bold',
      fontSize: 16,
      letterSpacing: 0.8,
      '.-price': { fontSize: 22, letterSpacing: 1.1 },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      '.detail-block': { paddingLeft: 18, paddingRight: 18 },
      '.total-block': { paddingLeft: 18, paddingRight: 18 },
    },
  }),
)
const paymentMethodBlock = css(
  blockLayout,
  {
    height: 526,
    padding: 24,
    '.payment-title': {
      textAlign: 'center',
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.6,
      color: '#676767',
      paddingTop: 8,
      lineHeight: 1.5,
    },
    '.brand-image-block': { marginTop: 8 },
    '.brand-image-block .payment-title': { letterSpacing: 0.6 },
    '.brand-image-block > img': {
      display: 'block',
      width: 360,
      maxWidth: '100%',
      margin: '0 auto',
    },
    '.brand-image-block .qr-sub-image-block': {
      height: 69,
      img: {
        height: 68,
        objectFit: 'contain',
      },
    },
    '.credit-block': {
      marginTop: 22,
      border: '1px solid #CCCCCC',
      borderRadius: 10,
      img: {
        display: 'block',
        width: 365,
        maxWidth: '100%',
        height: 55,
        margin: '0 auto',
        objectFit: 'contain',
        padding: '0 8px 12px',
      },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { padding: '25px 16px 16px' },
    landscape13: {
      '.credit-block': { width: 'calc(100% - 4px)', marginTop: 24 },
      '.brand-image-block': { width: 'calc(100% - 4px)' },
    },
  }),
)

const paymentButtonsStyle = css(
  {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 16,
    '&.two-columns': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { gap: 24 },
  }),
)
